export const DEFAULT_CENTER = [-34.401709, -58.6535575]

const MAPBOX_TOKEN = process.env.VUE_APP_MAPBOX_TOKEN

export const TILE_LAYERS = {
  // ── Calles ────────────────────────────────────────────────────────────────
  'OpenStreetMap': {
    url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
  },
  // Nota: CARTO (basemaps.cartocdn.com) pasó a exigir API key y devuelve una
  // marca de agua "API KEY REQUIRED". Se reemplaza por proveedores sin key.
  'OSM Standard': {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  // Esri Dark Gray sólo tiene tiles hasta z16: más allá se reescalan.
  'Esri Dark Gray': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, OpenStreetMap contributors',
    maxNativeZoom: 16,
    maxZoom: 20,
  },
  // Nota: los tiles de Stadia devuelven 401 fuera de localhost sin una API
  // key (no configurada en este proyecto). No usar como capa por defecto.
  'Stadia Alidade Smooth': {
    url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
  },
  'Stadia Alidade Smooth Dark': {
    url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
  },
  'OSM Hot': {
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles by <a href="https://www.hotosm.org/">HOT</a>',
    maxZoom: 19,
  },

  // ── Google ────────────────────────────────────────────────────────────────
  'Google': {
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    attribution: '&copy; <a href="https://www.google.com/maps">Google</a>',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    maxZoom: 20,
  },

  // ── Satélite ──────────────────────────────────────────────────────────────
  'ESRI Satellite': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
  'ESRI Satellite Labels': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
    maxZoom: 19,
    opacity: 0.7,
  },

  // ── Mapbox (requiere VUE_APP_MAPBOX_TOKEN) ────────────────────────────────
  ...(MAPBOX_TOKEN ? {
    'Mapbox Streets': {
      url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
      attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      tileSize: 512,
      zoomOffset: -1,
      maxZoom: 22,
    },
    'Mapbox Satellite': {
      url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
      attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      tileSize: 512,
      zoomOffset: -1,
      maxZoom: 22,
    },
  } : {}),
}

// ── Presets para páginas públicas ────────────────────────────────────────────

export const STREET_LAYER = MAPBOX_TOKEN
  ? TILE_LAYERS['Mapbox Streets']
  : TILE_LAYERS['OSM Standard']

// Variante oscura del callejero, usada automáticamente cuando la app está en
// modo oscuro (ver useMapTiles.js y store/map.js). Se usa Esri (no Stadia)
// porque no requiere API key: los tiles de Stadia devuelven 401 fuera de
// localhost sin una key configurada (VUE_APP_MAPBOX_TOKEN no aplica acá).
export const DARK_STREET_LAYER = TILE_LAYERS['Esri Dark Gray']

export const SATELLITE_LAYER = MAPBOX_TOKEN
  ? TILE_LAYERS['Mapbox Satellite']
  : TILE_LAYERS['ESRI Satellite']

// ── Google (usados cuando el usuario activa la preferencia global) ───────────
export const GOOGLE_STREET = TILE_LAYERS['Google']

export const GOOGLE_SATELLITE = {
  url: 'https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', // híbrido: satélite + etiquetas
  attribution: '&copy; <a href="https://www.google.com/maps">Google</a>',
  subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
  maxZoom: 20,
}

// ── Variantes "limpias": sólo calles, sin negocios ni puntos de interés ──────
// Google acepta estilos en el parámetro `apistyle`: s.t:2 = POI (negocios,
// lugares) y s.t:4 = transporte público, ambos con visibilidad off.
const GOOGLE_SIN_POI = '&apistyle=' + encodeURIComponent('s.t:2|p.v:off,s.t:4|p.v:off')
export const GOOGLE_STREET_CLEAN = { ...GOOGLE_STREET, url: GOOGLE_STREET.url + GOOGLE_SIN_POI }
export const GOOGLE_SATELLITE_CLEAN = { ...GOOGLE_SATELLITE, url: GOOGLE_SATELLITE.url + GOOGLE_SIN_POI }
// Mapbox Streets trae negocios; OSM Standard muestra pocos POIs.
export const STREET_LAYER_CLEAN = TILE_LAYERS['OSM Standard']
